import { apiSlice } from "../../app/api/apiSlice";

const EMPTY_ARRAY = [];

const toId = (value) => {
  if (!value) return "";
  return String(value?._id || value?.id || value);
};

const toSiteRef = (site) => {
  if (!site) return null;
  return {
    _id: toId(site),
    siteName: String(site?.siteName || ""),
  };
};

const recomputeCounts = (draftTraining) => {
  const attendees = Array.isArray(draftTraining?.attendees)
    ? draftTraining.attendees
    : EMPTY_ARRAY;
  draftTraining.attendeeCount = attendees.length;
  draftTraining.presentCount = attendees.reduce(
    (sum, attendee) => sum + (attendee?.present ? 1 : 0),
    0,
  );
};

const applyServerTraining = (draftTraining, training) => {
  if (!training) return;
  Object.assign(draftTraining, training);
};

const buildTrainingsParams = (filters = {}) => {
  const params = {};
  if (filters?.siteId) params.siteId = filters.siteId;
  if (filters?.from) params.from = filters.from;
  if (filters?.to) params.to = filters.to;
  return params;
};

export const trainingsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTrainings: builder.query({
      query: (filters = {}) => ({
        url: "/trainings",
        params: buildTrainingsParams(filters),
      }),
      providesTags: (result) =>
        result?.length
          ? [
              ...result.map((training) => ({
                type: "Training",
                id: training?._id,
              })),
              { type: "Training", id: "LIST" },
            ]
          : [{ type: "Training", id: "LIST" }],
    }),
    getTraining: builder.query({
      query: (id) => `/trainings/${id}`,
      transformResponse: (response) => response?.training || null,
      providesTags: (_result, _error, id) => [{ type: "Training", id }],
    }),
    createTraining: builder.mutation({
      query: (payload) => ({
        url: "/trainings",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Training", id: "LIST" }],
    }),
    updateTraining: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/trainings/${id}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Training", id },
        { type: "Training", id: "LIST" },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.training) {
            dispatch(
              apiSlice.util.updateQueryData("getTraining", id, (draft) => {
                applyServerTraining(draft, data.training);
              }),
            );
          }
        } catch (error) {
          // handled by calling component
        }
      },
    }),
    deleteTraining: builder.mutation({
      query: (id) => ({
        url: `/trainings/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Training", id },
        { type: "Training", id: "LIST" },
      ],
    }),
    addTrainingAttendees: builder.mutation({
      query: ({ id, memberIds }) => ({
        url: `/trainings/${id}/attendees`,
        method: "POST",
        body: { memberIds },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Training", id },
        { type: "Training", id: "LIST" },
      ],
      async onQueryStarted(
        { id, memberIds = EMPTY_ARRAY, memberSnapshots = EMPTY_ARRAY },
        { dispatch, queryFulfilled },
      ) {
        const nowIso = new Date().toISOString();
        const snapshotMap = new Map(
          memberSnapshots.map((member) => [toId(member), member]),
        );

        const patchResult = dispatch(
          apiSlice.util.updateQueryData("getTraining", id, (draft) => {
            if (!draft || !Array.isArray(draft.attendees)) return;
            const existingIds = new Set(
              draft.attendees.map((attendee) => String(attendee?.memberId)),
            );

            memberIds.forEach((rawMemberId) => {
              const memberId = String(rawMemberId || "");
              if (!memberId || existingIds.has(memberId)) return;

              const snapshot = snapshotMap.get(memberId) || {};
              const standortRef = toSiteRef(snapshot?.standortRef);
              draft.attendees.unshift({
                memberId,
                originSite: standortRef,
                addedBy: "",
                addedAt: nowIso,
                present: true,
                checkedInAt: nowIso,
                member: {
                  _id: memberId,
                  firstName: String(snapshot?.firstName || ""),
                  lastName: String(snapshot?.lastName || ""),
                  memberNumber: String(snapshot?.memberNumber || ""),
                  degree:
                    typeof snapshot?.degree === "number"
                      ? snapshot.degree
                      : snapshot?.degree ?? null,
                  standortRef,
                },
              });
              existingIds.add(memberId);
            });

            recomputeCounts(draft);
          }),
        );

        try {
          const { data } = await queryFulfilled;
          if (data?.training) {
            dispatch(
              apiSlice.util.updateQueryData("getTraining", id, (draft) => {
                applyServerTraining(draft, data.training);
              }),
            );
          }
        } catch (error) {
          patchResult.undo();
        }
      },
    }),
    patchTrainingAttendee: builder.mutation({
      query: ({ id, memberId, present }) => ({
        url: `/trainings/${id}/attendees/${memberId}`,
        method: "PATCH",
        body: { present },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Training", id },
        { type: "Training", id: "LIST" },
      ],
      async onQueryStarted(
        { id, memberId, present },
        { dispatch, queryFulfilled },
      ) {
        const nowIso = new Date().toISOString();
        const patchResult = dispatch(
          apiSlice.util.updateQueryData("getTraining", id, (draft) => {
            if (!draft || !Array.isArray(draft.attendees)) return;
            const attendee = draft.attendees.find(
              (item) => String(item?.memberId) === String(memberId),
            );
            if (!attendee) return;

            attendee.present = Boolean(present);
            if (attendee.present && !attendee.checkedInAt) {
              attendee.checkedInAt = nowIso;
            }

            recomputeCounts(draft);
          }),
        );

        try {
          const { data } = await queryFulfilled;
          if (data?.training) {
            dispatch(
              apiSlice.util.updateQueryData("getTraining", id, (draft) => {
                applyServerTraining(draft, data.training);
              }),
            );
          }
        } catch (error) {
          patchResult.undo();
        }
      },
    }),
    removeTrainingAttendee: builder.mutation({
      query: ({ id, memberId }) => ({
        url: `/trainings/${id}/attendees/${memberId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Training", id },
        { type: "Training", id: "LIST" },
      ],
      async onQueryStarted({ id, memberId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          apiSlice.util.updateQueryData("getTraining", id, (draft) => {
            if (!draft || !Array.isArray(draft.attendees)) return;
            draft.attendees = draft.attendees.filter(
              (attendee) => String(attendee?.memberId) !== String(memberId),
            );
            recomputeCounts(draft);
          }),
        );

        try {
          const { data } = await queryFulfilled;
          if (data?.training) {
            dispatch(
              apiSlice.util.updateQueryData("getTraining", id, (draft) => {
                applyServerTraining(draft, data.training);
              }),
            );
          }
        } catch (error) {
          patchResult.undo();
        }
      },
    }),
    searchTrainingMembers: builder.query({
      query: ({ q, limit = 10 }) => ({
        url: "/member/search",
        params: { q, limit },
      }),
      keepUnusedDataFor: 30,
    }),
  }),
});

export const {
  useGetTrainingsQuery,
  useGetTrainingQuery,
  useCreateTrainingMutation,
  useUpdateTrainingMutation,
  useDeleteTrainingMutation,
  useAddTrainingAttendeesMutation,
  usePatchTrainingAttendeeMutation,
  useRemoveTrainingAttendeeMutation,
  useSearchTrainingMembersQuery,
} = trainingsApiSlice;
